import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { withQueryClient } from 'common-nextjs';
import { useRouter } from 'next/router';
import { ParsedUrlQuery } from 'querystring';
import { useMount } from 'react-use';
import { Panel } from 'ui';
import SportsEngineSoftLanding from '~/components/ItemDetails/SportsEngineSoftLanding';
import Layout from '~/components/Layout';
import LockerLink from '~/components/Links/LockerLink';
import LockerFeedbackList from '~/components/NewLockers/LockerFeedbackList';
import LockerMeta from '~/components/NewLockers/LockerMeta';
import LockerPageContainer from '~/components/NewLockers/LockerPageContainer';
import NumberedPaginator from '~/components/Pagination/NumberedPaginator';
import {
  bigCatLockerBottomPageViewId,
  bigCatLockerTopPageViewId,
} from '~/constants/bigCatHardcodedLockerPageViewIds';
import useIsMyLocker from '~/hooks/lockers/useIsMyLocker';
import useLockerFeedback, {
  prefetchLockerFeedback,
} from '~/hooks/lockers/useLockerFeedback';
import { prefetchLockerUser } from '~/hooks/lockers/useLockerUser';
import { prefetchPublicProfile } from '~/hooks/lockers/usePublicProfile';
import { prefetchPageViewById } from '~/hooks/usePageView';
import { visitedLocker } from '~/services/analytics/events/generic';
import { CustomNextPage } from '~/typings/custom-next';
import bigCatUserId from '../../../constants/bigCatUserId';

export interface LockerFeedbackQuery extends ParsedUrlQuery {
  page: string;
  username: string;
}

interface Props {}

const LockerFeedbackPage: CustomNextPage<Props> = () => {
  const myLocker = useIsMyLocker();
  useMount(() => visitedLocker('feedback', myLocker));
  const router = useRouter();

  const { data: userFeedback } = useLockerFeedback();

  return (
    <Layout
      title={`Feedback - ${router.query.username}`}
      excludeMobileSearch
      container={false}
    >
      <LockerMeta />
      <SportsEngineSoftLanding />
      <LockerPageContainer>
        <div className="mx-auto max-w-lg">
          <div className="mb-4 flex items-center">
            <div className="flex-1">
              <LockerLink
                username={router.query.username as string}
                className="flex items-center space-x-2 text-sm"
              >
                <ArrowLeftIcon className="h-3 w-3" /> <span>View Items</span>
              </LockerLink>
            </div>

            <div className="font-semibold">Feedback</div>
            <div className="flex-1" />
          </div>

          <Panel>
            {userFeedback && (
              <LockerFeedbackList
                feedbackData={userFeedback.data}
                showRatingType
              />
            )}
          </Panel>

          {userFeedback && (
            <NumberedPaginator paging={userFeedback.meta.paging} />
          )}
        </div>
      </LockerPageContainer>
    </Layout>
  );
};

LockerFeedbackPage.getInitialProps = withQueryClient(
  queryClient => async ctx => {
    const query = ctx.query as LockerFeedbackQuery;
    const [user] = await Promise.all([
      prefetchLockerUser(query.username, queryClient, ctx),
      prefetchPublicProfile(query.username, queryClient),
      prefetchLockerFeedback(query, queryClient, ctx),
    ] as const);

    if (user.id === bigCatUserId) {
      await Promise.all([
        prefetchPageViewById(bigCatLockerBottomPageViewId, queryClient, ctx),
        prefetchPageViewById(bigCatLockerTopPageViewId, queryClient, ctx),
      ]);
    }

    return {};
  },
);

export default LockerFeedbackPage;
